import { Switch, Stack, Typography, InputAdornment } from '@mui/material';
import { WarningRounded } from '@mui/icons-material';

import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { pxToRem } from '~/theme/typography';
import { admin } from '~/components/Admin/AdminDialog/AdminDialog.style';
import { cell } from '~/components/Admin/ResourceTable/ResourceTable.style';
import { ACTION_STATUS } from '~/components/Admin/ResourceTable';
import { useFormik } from 'formik';
import { AdminDialogActions } from '~/components/Admin/AdminDialog/AdminDialogActions/AdminDialogActions';
import {
  useDropDownListQuery,
  useGetKnowledgeQuery,
  useUpdateByOriginalFileIdMutation,
} from '~/api/oneBoxV3';
import {
  TOneBoxV3GetKnowledgePayload,
  TUniqResourceProps,
  resourceCategory,
} from '~/api/oneBoxV3/oneBoxV3.type';
import { SearchCategory, adminTab } from '~/types/enum';
import { TAdminResource } from '~/store/resource/resource.type';
import { defaultOption } from '~/utils/constants';
import { formatTime } from '~/utils/helper/format';
import { THandleEditResourceForm } from '~/components/Admin/AdminComponent.type';
import { defaultEditResourceForm } from '~/components/Admin/AdminComponent.constant';
import {
  EditDialogAutocomplete,
  SchoolEditDialogAutocomplete,
} from './EditDialogAutocomplete';
import { getterChaptersToKnowledge } from '~/store/resource/resourceGetter';
import { chain } from 'lodash';

type TProps = {
  editedResource: TAdminResource;
  tab: adminTab;
  handleClose: () => void;
};

const labelProps = (label: string) => ({
  label: <admin.InfoTitle>{label}</admin.InfoTitle>,
  labelPlacement: 'start' as const,
});

export const EditDialogContent = ({
  editedResource,
  tab,
  handleClose,
}: TProps) => {
  const [dropDownList, setDropDownList] =
    useState<TUniqResourceProps>(defaultOption);
  const downloadListPayload = {
    categories: [editedResource.categories.origin as unknown as SearchCategory], //[SearchCategory.CRAM_SCHOOL],
    resourceProps: [
      'versions',
      'years',
      'eduCodes',
      'subjects',
      'grades',
      'resources1',
      'resources2',
      'resources3',
    ],
  };
  const { data: dropDownListApiData } =
    useDropDownListQuery(downloadListPayload);
  const [updateResourceApi] = useUpdateByOriginalFileIdMutation();

  const knowledgePayload: TOneBoxV3GetKnowledgePayload = {
    version: editedResource.versions.origin,
    year: editedResource.years.origin,
    eduCode: editedResource.eduCodes.origin,
    subject: editedResource.subjects.origin,
    grade: editedResource.grades.origin,
  };
  useGetKnowledgeQuery(knowledgePayload);
  const chapters = useSelector(state =>
    getterChaptersToKnowledge(state, knowledgePayload),
  );
  const fileType = chain(editedResource.fileName).split('.').last().value();
  const fileExtension = `.${fileType}`;

  const formik = useFormik({
    initialValues: defaultEditResourceForm,
    onSubmit: async values => {
      const newValue = {
        ...values,
        displayName: values.displayName + fileExtension,
      };
      const { err, data } = await updateResourceApi(newValue).unwrap();
      if (err === 4001 && typeof data !== 'undefined') {
        const { errorResourceColumns } = data;
        errorResourceColumns.forEach(errColumn => {
          formik.setFieldError(errColumn, '此資料格式有誤或不存在，請重新輸入');
        });
        return;
      }
      handleClose();
    },
  });

  const { values, errors, touched, isSubmitting, setValues, setFieldValue } =
    formik;
  const errorProps = (key: keyof typeof formik.values) => ({
    error: touched[key] && Boolean(errors[key]),
    helperText: touched[key] && errors[key] && (
      <Stack direction="row" alignItems="center">
        <WarningRounded sx={{ fontSize: '16px' }} />
        {errors[key]}
      </Stack>
    ),
  });

  const infos = useMemo(() => {
    const { fileName, url, createdAt } = editedResource;
    return [
      { title: '檔名', content: fileName },
      { title: 'URL', content: url },
      {
        title: '上傳時間',
        content: formatTime(createdAt),
      },
    ];
  }, [editedResource]);

  const handleEdit = ({ newValue, id }: THandleEditResourceForm) => {
    if (id === 'chapterKeyword') {
      const { knowledge: prevKnowledge, chapterKeyword: prevChapterKeyword } =
        values;
      // 要移除的知識點
      const deleteKeywords = prevChapterKeyword.filter(
        value => !newValue.includes(value),
      );
      const deleteKnowledge = deleteKeywords.reduce((acc: string[], value) => {
        return [...acc, ...(chapters[value] ? chapters[value] : [])];
      }, []);
      // 要新增的知識點
      const addKnowledge = (newValue as string[]).reduce(
        (acc: string[], value) => {
          return [...acc, ...(chapters[value] ? chapters[value] : [])];
        },
        [],
      );

      let updatedKnowledge = prevKnowledge.filter(
        item => !deleteKnowledge.includes(item),
      );
      updatedKnowledge = [...updatedKnowledge, ...addKnowledge];
      void setFieldValue('knowledge', [...new Set(updatedKnowledge)]);
    }
    const initValue = editedResource[`${id}`].origin
      ? [editedResource[`${id}`].origin]
      : [];
    void setFieldValue(id, newValue.length > 0 ? newValue : initValue);
  };
  useEffect(() => {
    if (!dropDownListApiData || !dropDownListApiData.data) return;
    const data = dropDownListApiData.data.uniqResourceProps;
    setDropDownList(prev => ({ ...prev, ...data }));
  }, [dropDownListApiData]);

  useEffect(() => {
    if (!editedResource) return;
    const {
      originalFileId,
      categories,
      versions,
      years,
      eduCodes,
      isLaunch,
      grades,
      subjects,
      resources1,
      resources2,
      resources3,
      chapterKeyword,
      knowledge,
      displayName,
    } = editedResource;

    void setValues({
      originalFileId: originalFileId,
      isLaunch: isLaunch,
      categories: [categories.origin, ...categories.additional],
      versions: [versions.origin, ...versions.additional],
      years: [years.origin, ...years.additional],
      eduCodes: [eduCodes.origin, ...eduCodes.additional],
      grades: [grades.origin, ...grades.additional],
      subjects: [subjects.origin, ...subjects.additional],
      resources1: resources1.origin
        ? [resources1.origin, ...resources1.additional]
        : resources1.additional,
      resources2: resources2.origin
        ? [resources2.origin, ...resources2.additional]
        : resources2.additional,
      resources3: resources3.origin
        ? [resources3.origin, ...resources3.additional]
        : resources3.additional,
      chapterKeyword: chapterKeyword.origin
        ? [chapterKeyword.origin, ...chapterKeyword.additional]
        : chapterKeyword.additional,
      knowledge: knowledge.origin
        ? [knowledge.origin, ...knowledge.additional]
        : knowledge.additional,
      displayName: displayName.split(fileExtension)[0],
    });
  }, [editedResource]);

  return (
    <>
      <admin.DialogContent>
        <admin.ContentContainer>
          <admin.ContentTitle>檔案資訊</admin.ContentTitle>
          <admin.ContentSubContainer>
            {infos.map(item => (
              <admin.InfoContainer key={item.title}>
                <admin.InfoTitle>{item.title}</admin.InfoTitle>
                <admin.InfoContent>
                  {item.title === '檔名' && (
                    <cell.TypeChip
                      size="small"
                      label={editedResource.categories.origin}
                      type={editedResource.categories.origin}
                    />
                  )}
                  {item.content}
                </admin.InfoContent>
              </admin.InfoContainer>
            ))}
          </admin.ContentSubContainer>
        </admin.ContentContainer>
        <admin.ContentContainer>
          <admin.ContentTitle>檔案設定</admin.ContentTitle>
          <admin.ContentSubContainer>
            <admin.FormControlLabel
              control={
                <>
                  <admin.InfoContent
                    sx={{ marginLeft: '-16px', fontSize: pxToRem(14) }}
                  >
                    {values.isLaunch ? '上架中' : '已下架'}
                  </admin.InfoContent>
                  <Switch
                    checked={values.isLaunch}
                    color="info"
                    onChange={formik.handleChange}
                    id="isLaunch"
                  />
                </>
              }
              {...labelProps('狀態*')}
            />
            {tab === adminTab.school && (
              <admin.FormControlLabel
                control={
                  <SchoolEditDialogAutocomplete
                    id="categories"
                    values={values}
                    customOptions={[
                      resourceCategory.CRAM_SCHOOL,
                      resourceCategory.SCHOOL,
                    ]}
                    editedResource={editedResource}
                    handleEdit={handleEdit}
                    errorProps={errorProps}
                  />
                }
                {...labelProps('類別*')}
              />
            )}
            {tab === adminTab.cramSchools && (
              <admin.FormControlLabel
                control={
                  <EditDialogAutocomplete
                    id="categories"
                    values={values}
                    customOptions={[
                      resourceCategory.CRAM_SCHOOL,
                      resourceCategory.SCHOOL,
                    ]}
                    editedResource={editedResource}
                    handleEdit={handleEdit}
                    errorProps={errorProps}
                  />
                }
                {...labelProps('類別*')}
              />
            )}
            <admin.FormControlLabel
              control={
                <EditDialogAutocomplete
                  id="versions"
                  values={values}
                  customOptions={dropDownList.versions ?? []}
                  editedResource={editedResource}
                  handleEdit={handleEdit}
                  errorProps={errorProps}
                />
              }
              {...labelProps('版本*')}
            />
            <admin.FormControlLabel
              control={
                <EditDialogAutocomplete
                  id="years"
                  values={values}
                  customOptions={dropDownList.years ?? []}
                  editedResource={editedResource}
                  handleEdit={handleEdit}
                  errorProps={errorProps}
                />
              }
              {...labelProps('學年度*')}
            />
            <admin.FormControlLabel
              control={
                <EditDialogAutocomplete
                  id="eduCodes"
                  values={values}
                  customOptions={dropDownList.eduCodes ?? []}
                  editedResource={editedResource}
                  handleEdit={handleEdit}
                  errorProps={errorProps}
                />
              }
              {...labelProps('學制*')}
            />
            <admin.FormControlLabel
              control={
                <EditDialogAutocomplete
                  id="subjects"
                  values={values}
                  customOptions={dropDownList.subjects ?? []}
                  editedResource={editedResource}
                  handleEdit={handleEdit}
                  errorProps={errorProps}
                />
              }
              {...labelProps('科目*')}
            />
            <admin.FormControlLabel
              control={
                <EditDialogAutocomplete
                  id="grades"
                  values={values}
                  customOptions={dropDownList.grades ?? []}
                  editedResource={editedResource}
                  handleEdit={handleEdit}
                  errorProps={errorProps}
                />
              }
              {...labelProps('年級*')}
            />
            <admin.FormControlLabel
              control={
                <EditDialogAutocomplete
                  id="resources1"
                  values={values}
                  customOptions={dropDownList.resources1 ?? []}
                  editedResource={editedResource}
                  handleEdit={handleEdit}
                  errorProps={errorProps}
                />
              }
              {...labelProps('資源類別１*')}
            />
            <admin.FormControlLabel
              control={
                <EditDialogAutocomplete
                  id="resources2"
                  values={values}
                  customOptions={dropDownList.resources2 ?? []}
                  editedResource={editedResource}
                  handleEdit={handleEdit}
                  errorProps={errorProps}
                  isFreesolo={true}
                />
              }
              {...labelProps('資源類別２')}
            />
            <admin.FormControlLabel
              control={
                <EditDialogAutocomplete
                  id="resources3"
                  values={values}
                  customOptions={dropDownList.resources3 ?? []}
                  editedResource={editedResource}
                  handleEdit={handleEdit}
                  errorProps={errorProps}
                  isFreesolo={true}
                />
              }
              {...labelProps('資源類別３')}
            />
            <admin.FormControlLabel
              control={
                <EditDialogAutocomplete
                  id="chapterKeyword"
                  values={values}
                  customOptions={Object.keys(chapters)}
                  editedResource={editedResource}
                  handleEdit={handleEdit}
                  errorProps={errorProps}
                  isFreesolo={true}
                />
              }
              {...labelProps('章節關鍵字')}
            />
            <Typography variant="caption" ml={17}>
              輸入章節關鍵字後，系統將自動帶入知識點到下方欄位內
            </Typography>
            <admin.FormControlLabel
              control={
                <EditDialogAutocomplete
                  id="knowledge"
                  values={values}
                  customOptions={[]}
                  editedResource={editedResource}
                  handleEdit={handleEdit}
                  errorProps={errorProps}
                  isFreesolo={true}
                />
              }
              {...labelProps('知識點')}
            />
            <admin.FormControlLabel
              control={
                <admin.TextField
                  id="displayName"
                  size="small"
                  value={values.displayName}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        {fileExtension}
                      </InputAdornment>
                    ),
                  }}
                  onChange={formik.handleChange}
                />
              }
              {...labelProps('顯示名稱')}
            />
          </admin.ContentSubContainer>
        </admin.ContentContainer>
      </admin.DialogContent>
      <AdminDialogActions
        handleClose={handleClose}
        handleSubmit={() => formik.handleSubmit()}
        status={ACTION_STATUS.EDIT}
        acceptButtonText="儲存變更"
        disabled={isSubmitting}
      />
    </>
  );
};
